import { Injectable } from '@angular/core'
import {
  Observable,
  combineLatest,
  concatMap,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { CustomersService } from './customers.service'
import { Page } from '../../models/util.model'
import { CountriesService, Country } from '../countries'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  CustomerModalCode,
  CustomerNotificationCode,
  parseCustomerModal,
  parseCustomerNotification,
} from './libs/customer-notification.lib'
import { CustomerSearchParams } from './models/customer-search.model'
import {
  CustomersListingData,
  CustomersListingPage,
} from './models/customer-listing.model'
import { Customer } from './models/customer.entity'
import { parseCustomerPageKeys } from './libs/customer-search.lib'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class CustomersRepository {
  constructor(
    private customersService: CustomersService,
    private countriesService: CountriesService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search customers and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search customers
   */
  searchCustomers$(
    searchParams: CustomerSearchParams,
    listingData?: CustomersListingData,
    returnAll?: boolean,
  ): Observable<CustomersListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.customersService.limit

    return needsPagination
      ? this._paginateCustomers$(searchParams, listingData)
      : this._searchCustomers$(searchParams, listingData, returnAll)
  }

  /**
   * Search customers with relative external data
   * @param searchParams - the search params
   * @param fields - the customers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<CustomersListingPage> for search customers
   */
  private _searchCustomers$(
    searchParams: CustomerSearchParams,
    listingData?: CustomersListingData,
    returnAll?: boolean,
  ): Observable<CustomersListingPage> {
    return this.customersService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page customers data
   * @param page - the customers page
   * @param fields - the customers fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Customer>,
    listingData?: CustomersListingData,
  ): Observable<CustomersListingData> {
    const extDataKeys = parseCustomerPageKeys(page)
    const obs$: Partial<{
      countries: Observable<Country[]>
    }> = {}

    if (extDataKeys.countryCodes) {
      const existingCountries = listingData?.countries || []
      const hasAllCountries = extDataKeys.countryCodes.every((code) =>
        existingCountries.some((country) => country.alpha2Code === code),
      )

      if (!hasAllCountries) {
        obs$.countries = this.countriesService.store$(
          extDataKeys.countryCodes,
          existingCountries,
        )
      } else {
        obs$.countries = of(existingCountries)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as CustomersListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: CustomersListingData = {}
        if (results.countries) extData.countries = results.countries
        return extData
      }),
    )
  }

  /**
   * Search more than 200 customers with relative external data
   * @param searchParams - the search params
   * @param fields - the customers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<CustomersListingPage> for search customers
   */
  private _paginateCustomers$(
    searchParams: CustomerSearchParams,
    listingData?: CustomersListingData,
  ): Observable<CustomersListingPage> {
    const totalCount = searchParams.limit || this.customersService.limit
    const pagesCount = Math.ceil(totalCount / this.customersService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.customersService.limit,
        offset: i * this.customersService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchCustomers$(pageParams, listingData),
      ),
      scan<CustomersListingPage, CustomersListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as CustomersListingPage,
      ),
      last(),
    )
  }

  notify(options: { code?: CustomerNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseCustomerNotification(options.code),
    )
  }

  async alert(
    code: CustomerModalCode,
    customer?: Customer,
  ): Promise<ModalResponse> {
    const settings = parseCustomerModal(code, customer)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
